import { i18nInstance } from '@/main'
import * as mutations from '@/store/mutation-types'

function normalizeReferences(text) {
  const patterns = [
    // Patterns to match and normalize
    { regex: /\(Quelle\s*(\d+)\)/g, replacement: '[Quelle $1]' }, // "(Quelle 1)" -> "[Quelle 1]"
    {
      regex: /\(Quelle\s*(\d+),\s*Quelle\s*(\d+)\)/g,
      replacement: '[Quelle $1][Quelle $2]'
    }, // "(Quelle 1, Quelle 2)" -> "[Quelle 1][Quelle 2]"
    {
      regex: /\[Quelle\s*(\d+),\s*Quelle\s*(\d+)\]/g,
      replacement: '[Quelle $1][Quelle $2]'
    }, // "[Quelle 1, Quelle 2]" -> "[Quelle 1][Quelle 2]"
    {
      regex: /\[Quelle\s*(\d+)\s*bis\s*(\d+)\]/g,
      replacement: '[Quelle $1]-[Quelle $2]'
    } // "[Quelle 1 bis 5]" -> "[Quelle 1]-[Quelle 5]"
  ]

  patterns.forEach(pattern => {
    text = text.replace(pattern.regex, pattern.replacement)
  })
  return text
}

const createDocumentIdReference = tooltips => {
  const documentIdReference = {}
  let currentNumber = 1 // Start numbering from 1

  Object.values(tooltips).forEach(tooltip => {
    const { document_id } = tooltip

    if (!documentIdReference.hasOwnProperty(document_id)) {
      documentIdReference[document_id] = currentNumber
      currentNumber++
    }
  })

  return documentIdReference
}

function getSourceType(tooltip) {
  // if segment not empty its a segment source
  if (tooltip.segment) {
    return 'segment'
  } else if (!(tooltip.segment && tooltip.metadata)) {
    return 'document'
  }

  return null
}

export function getDocumentShortNames(documents) {
  let shortDocumentMap = {}

  for (let id in documents) {
    if (documents.hasOwnProperty(id) && documents[id].shortcode) {
      shortDocumentMap[id] = documents[id].shortcode
    }
  }

  return shortDocumentMap
}

function applyOpacity(rgba, opacity) {
  // Extract the RGBA components from the input string
  const matches = rgba.match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*(?:\.\d+)?))?\s*\)$/i
  )

  if (!matches) {
    throw new Error('Invalid RGBA format')
  }

  // Adjust the alpha value
  return `rgba(${matches[1]}, ${matches[2]}, ${matches[3]}, ${opacity})`
}

export function addDocumentLinkToMessage(customData, message, store, smartDoc) {
  const sourceDocumentShortNames = store.state.sourceDocumentShortNames

  if (message.hasOwnProperty('text')) {
    let smartdocSegmentTooltips = {}
    if (customData.hasOwnProperty('smartdoc_segment_tooltips')) {
      smartdocSegmentTooltips = customData['smartdoc_segment_tooltips']
      store.commit(mutations.SET_LAST_SMARTDOC_SEGMENT_TOOLTIPS, customData)
    }

    for (let documentId in sourceDocumentShortNames) {
      const shortName = sourceDocumentShortNames[documentId]
      if (smartdocSegmentTooltips && message['text'].includes(shortName)) {
        if (!smartdocSegmentTooltips.hasOwnProperty(shortName)) {
          smartdocSegmentTooltips[shortName] = {
            document_id: documentId,
            page: shortName,
            segment: '',
            metadata: null,
            id: `${documentId}-0`
          }
        }
      }
    }

    if (smartdocSegmentTooltips) {
      let documentIdReference = createDocumentIdReference(
        smartdocSegmentTooltips
      )
      const listRegex = /<ul[^>]*>/g
      const sourcesRegex = /Quellen:\s*<ul[^>]*>(.*?)<\/ul>/g

      if (
        Boolean(message.text.match(listRegex)) &&
        Boolean(message.text.match(sourcesRegex))
      ) {
        message['text'] = message['text'].replace(
          /<ul[^>]*>/g,
          `<ul style='list-style:none;padding-left:0px;display: flex;align-items: center;gap:4px;flex-wrap:wrap;'>`
        )
      }
      message.text = normalizeReferences(message.text)

      const aTagPlaceholders = []
      let placeholderCounter = 0
      const placeholderPrefix = ' __PLACEHOLDER_'
      const placeholderSuffix = ' __'

      for (let i in smartdocSegmentTooltips) {
        const anchorId = smartdocSegmentTooltips[i].id
        const segment = smartdocSegmentTooltips[i].segment
        const metadata = smartdocSegmentTooltips[i].metadata
        const sourceType = getSourceType(smartdocSegmentTooltips[i])
        if (!sourceType) continue
        let documentId = null
        if (smartdocSegmentTooltips[i].document_id) {
          documentId = smartdocSegmentTooltips[i].document_id
        }
        const root = document.documentElement
        let currentColor = window
          .getComputedStyle(root)
          .getPropertyValue('--userMessageBackgroundColor')
          .trim()

        const matches = currentColor.match(
          /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*(?:\.\d+)?))?\s*\)$/i
        )
        if (!currentColor || !matches) {
          currentColor = 'rgba(0, 44, 103, 1)'
        }
        const colorWithNewOpacity = smartDoc
          ? 'var(--copilotSourceBackgroundColor)'
          : applyOpacity(currentColor, 0.125)
        const sourceColor = smartDoc
          ? 'var(--copilotSourceOnBackgroundColor)'
          : 'var(--userMessageBackgroundColor)'
        const referenceNumberColor = smartDoc
          ? 'var(--copilotInactiveSourceTextColor)'
          : '#414141'
        // line breaks and tabs lead to the onclick not working
        const encodedSegment = Buffer.from(segment).toString('base64')
        const cleanedUpSegment = segment
          .replace(/<br\s*\/?>|\n\n|\n|\r\n|\r/g, '')
          .replace(/^\t/, '')
          .replace(/'/g, '')
        const translation = Number.isInteger(smartdocSegmentTooltips[i].page)
          ? i18nInstance.t('page')
          : ''

        let uniquePageNumbers = [smartdocSegmentTooltips[i].page]
        if (metadata?.coordinates) {
          const pageNumbers = metadata?.coordinates?.map(
            coordinate => coordinate.page_number
          )
          uniquePageNumbers = [...new Set(pageNumbers)]
        }
        const pageNumbersToDisplay =
          uniquePageNumbers.length === 1
            ? uniquePageNumbers[0].toString()
            : uniquePageNumbers.join('&nbsp;&&nbsp;')

        const sourceArgs = {
          message: 'sourceClicked',
          anchorId: anchorId,
          pageNumber: smartdocSegmentTooltips[i].page,
          segment: encodedSegment,
          documentId: documentId,
          clickFromSourceList: !!documentIdReference,
          metadata: metadata
        }

        const payload = JSON.stringify(sourceArgs).replace(/'/g, '')
        message['documentReferences'] = documentIdReference
        const placeholder = `${placeholderPrefix}${placeholderCounter}${placeholderSuffix}`
        message['text'] = message['text'].replaceAll(i, `${placeholder}`)
        aTagPlaceholders.push({
          placeholder,
          original: `<a id='${anchorId}' data-documentId='${documentId ||
            'keine Id'}' onclick='goToSource(${payload})' title='${cleanedUpSegment}' class="inactive-document"  style='color: ${sourceColor};font-weight: 900;background-color: ${colorWithNewOpacity};padding:4px 4px 2px 4px;border-radius: 4px;white-space:nowrap;font-size:12px;'>${translation}&nbsp;${pageNumbersToDisplay}${
            documentIdReference
              ? `<sup style='font-weight: 500;color:${referenceNumberColor};'>&nbsp;${documentIdReference[documentId]}</sup>`
              : ''
          }</a>`
        })
        placeholderCounter++
      }
      aTagPlaceholders.forEach(({ placeholder, original }) => {
        message.text = message.text.replaceAll(placeholder, original)
      })
    }
  }
}
